import React, { createContext, useContext, useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { message } from 'antd';
import { useNoteStore } from '../stores/noteStore';
import { useSearchStore } from '../stores/searchStore';
import { filterNotes, sortNotes } from '../pages/DashboardUtils';
import { usePrivateKnowledgeBase } from './PrivateKnowledgeBaseContext';
import { useCollaborativeKnowledgeBase } from './CollaborativeKnowledgeBaseContext';
import apiClient from '../services/api';
import {
  getSelectedFolder,
  getSelectedTag,
  getSelectedTopic,
  getSelectedKB,
  getSearchKeyword,
  getSearchMode,
  getSelectedNote,
  getIsUserSelectedNote,
  getPagination,
  setSelectedFolder,
  setSelectedTag,
  setSelectedTopic,
  setSelectedKB,
  setSearchKeyword,
  setSearchMode,
  setSelectedNote,
  setIsUserSelectedNote,
  setPagination,
  addGlobalStateListener
} from '../hooks/useDashboardGlobalVar';
import '../css/DeleteConfirmModal.css';

// 定义分页限制常量，从环境变量读取，默认20
const pageLimit = parseInt(process.env.REACT_APP_FETCH_LIMIT || process.env.FETCH_LIMIT || '20', 10);

// 自定义删除确认模态框组件
const DeleteConfirmModal = ({ isVisible, noteTitle, onConfirm, onCancel }) => {
  const { t } = useTranslation(['common', 'messages']);
  if (!isVisible) return null;

  return (
    <div className="custom-modal-overlay">
      <div className="custom-modal-content">
        <div className="modal-header">
          <div className="modal-icon">
            ⚠️
          </div>
          <h3>{t('messages:confirm_delete_note', { defaultValue: 'Confirm Delete Note' })}</h3>
        </div>

        <div className="modal-body">
          <p>
            {t('messages:confirm_delete_note_content', { defaultValue: 'Are you sure you want to delete note "{{title}}"?', title: noteTitle })}
          </p>
          <p>
            {t('messages:delete_irreversible', { defaultValue: 'This operation cannot be undone. The note will be permanently lost after deletion.' })}
          </p>
        </div>

        <div className="modal-footer">
          <button onClick={onCancel}>
            {t('common:actions.cancel', { defaultValue: 'Cancel' })}
          </button>
          <button onClick={onConfirm}>
            {t('common:actions.confirm_delete', { defaultValue: 'Confirm Delete' })}
          </button>
        </div>
      </div>
    </div>
  );
};

const NoteListContext = createContext();

export const useNoteList = () => {
  const context = useContext(NoteListContext);
  if (!context) {
    throw new Error('useNoteList must be used within a NoteListProvider');
  }
  return context;
};

export const NoteListProvider = ({ children }) => {
  const { t } = useTranslation(['messages']);

  // Note store hooks
  const { notes, pinnedNotes, fetchNotes, loadMoreNotes, resetState, updateNote, deleteNote } = useNoteStore();
  const { searchResults, isLoading: isSearchLoading, semanticSearch, search, clearSearchResults, loadMoreSearchResults, forceCleanAll } = useSearchStore();

  // Get private and collaborative knowledge base data
  const { privateTopics, fetchTopicNotesCountForTree: fetchPrivateTopicNotesCount } = usePrivateKnowledgeBase();
  const { collaborativeKBTopics, fetchTopicNotesCountForTree: fetchCollaborativeTopicNotesCount } = useCollaborativeKnowledgeBase();

  // Delete confirmation modal state
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);


  // UI state
  const noteListRef = useRef(null);

  // 防重复执行状态引用
  const lastExecutedStateRef = useRef(null);

  // 快捷操作切换标志，防止重复执行
  const isQuickActionSwitchingRef = useRef(false);

  // 自动选中新建笔记的处理标志，防止重复处理
  const autoSelectNoteRef = useRef(false);

  // 组件挂载时，如果有createNoteId标志，重置处理标志（允许处理）
  useEffect(() => {
    const createNoteId = localStorage.getItem('createNoteId');
    if (createNoteId) {
      console.log('[NoteListContext] 组件挂载，检测到createNoteId，重置处理标志');
      autoSelectNoteRef.current = false;
    }
  }, []); // 只在组件挂载时执行一次

  // Search type selection (fixed)
  const searchTypeSelection = 'hybrid';

  // 防重复调用：存储上次搜索参数
  const lastSearchParamsRef = useRef(null);

  // 辅助函数：根据 selectedFolder 获取对应的 filterType
  const getFilterTypeFromFolder = useCallback((folder) => {
    return folder === 'private' ? 'private' :
      folder === 'collaborative' ? 'collaborative' :
        folder === 'pinned' ? 'pinned' :
          folder === 'recent' ? 'recent' : 'all';
  }, []);

  // Combine pinned and regular notes
  const allNotes = useMemo(() => {
    return [...pinnedNotes, ...notes];
  }, [pinnedNotes, notes]);

  // Sort notes based on selected folder
  const sortedNotes = useMemo(() => {
    return sortNotes(allNotes, getSelectedFolder());
  }, [allNotes, getSelectedFolder()]);

  // Display notes based on search or filter
  const displayNotes = useMemo(() => {
    const currentSearchKeyword = getSearchKeyword();
    const currentSelectedFolder = getSelectedFolder();
    const currentSelectedTag = getSelectedTag();
    const currentSelectedTopic = getSelectedTopic();
    const currentSelectedKB = getSelectedKB();

    // 如果有关键词搜索，直接返回搜索结果
    if (currentSearchKeyword && currentSearchKeyword.trim()) {
      return searchResults;
    }

    // 否则执行前端筛选
    return filterNotes(sortedNotes, {
      searchKeyword: '', // When using search results, don't filter on frontend
      selectedFolder: currentSelectedFolder,
      selectedTag: currentSelectedTag,
      selectedTopic: currentSelectedTopic,
      selectedKB: currentSelectedKB,
      privateTopics,
      collaborativeKBTopics,
      oneWeekAgo: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000), // 7 days ago
      allKnowledgeBases: [] // This will be populated when needed
    });
  }, [
    getSearchKeyword(),
    searchResults,
    sortedNotes,
    getSelectedFolder(),
    getSelectedTag(),
    getSelectedTopic(),
    getSelectedKB(),
    privateTopics,
    collaborativeKBTopics
  ]);

  // Set callback for updating sidebar stats
  const noteOperationCallbackRef = useRef(null);

  const setNoteOperationCallback = useCallback((callback) => {
    noteOperationCallbackRef.current = callback;
  }, []);

  // Execute note operation callback
  const executeNoteOperationCallback = useCallback(() => {
    if (noteOperationCallbackRef.current) {
      // 检查是否是新的回调结构
      if (typeof noteOperationCallbackRef.current === 'object' && noteOperationCallbackRef.current.onOperationComplete) {
        noteOperationCallbackRef.current.onOperationComplete();
      } else if (typeof noteOperationCallbackRef.current === 'function') {
        // 兼容旧的函数形式
        noteOperationCallbackRef.current();
      }
    }
  }, []);

  // Handle note click from note list
  const handleNoteClick = useCallback(async (note) => {
    console.log('[NoteList] 用户点击笔记:', note.id);
    setIsUserSelectedNote(true);
    setSelectedNote(note);

    // 如果提供了 onNoteSelect 回调，同步更新父组件的状态
    if (noteOperationCallbackRef.current && typeof noteOperationCallbackRef.current.onNoteSelect === 'function') {
      noteOperationCallbackRef.current.onNoteSelect(note);
    }
  }, []);

  // Show delete confirmation modal
  const showDeleteConfirmModal = useCallback(() => {
    if (!getSelectedNote()) {
      message.warning(t('messages:please_select_note_to_delete', { defaultValue: 'Please select a note to delete' }));
      return;
    }
    setIsDeleteModalVisible(true);
  }, []);

  // Handle delete confirmation
  const handleDeleteConfirm = useCallback(async () => {
    if (!getSelectedNote()) {
      message.warning(t('messages:please_select_note_to_delete', { defaultValue: 'Please select a note to delete' }));
      return;
    }

    try {
      await deleteNote(getSelectedNote().id);

      // Clear selected note if it was deleted
      if (getSelectedNote()) {
        setSelectedNote(null);
      }

      // 刷新笔记列表 - 保持当前的选择条件和搜索关键词
      console.log('[NoteListContext] 删除笔记成功后，开始刷新笔记列表，保持当前筛选条件:', {
        searchKeyword: getSearchKeyword(),
        selectedFolder: getSelectedFolder(),
        selectedTag: getSelectedTag()?.name,
        selectedTopic: getSelectedTopic()?.name,
        selectedKB: getSelectedKB()?.name
      });

      // 注意：删除笔记后，全局状态监听器会自动处理搜索更新
      // 这里不需要手动刷新，避免重复请求和过早刷新
      console.log('[NoteListContext] 删除笔记成功，全局状态监听器将自动处理搜索更新');

      // 清空选中笔记
      setSelectedNote(null);

      // 跳过手动刷新逻辑，让全局状态监听器处理
      if (false) { // 禁用手动刷新
        console.log('[NoteListContext] 保持搜索关键词，重新执行搜索:', getSearchKeyword());

        // 根据当前选择的条件执行搜索
        const topicId = getSelectedTopic() ? getSelectedTopic().topic_id : '';
        const kbId = getSelectedKB() ? getSelectedKB().kb_id : null;
        const folder = getSelectedFolder() !== 'all' ? getSelectedFolder() : 'all';
        const filterType = getFilterTypeFromFolder(folder);

        // 统一使用混合搜索模式
        console.log('[NoteListContext] 使用混合搜索模式刷新:', {
          query: getSearchKeyword().trim(),
          folder: folder,
          filterType: filterType,
          topicId: topicId,
          kbId: kbId,
          searchType: searchTypeSelection
        });
        await semanticSearch(getSearchKeyword().trim(), 1, pageLimit, filterType, topicId, kbId, searchTypeSelection, true); // 添加reset=true参数
      } else {
        // 没有搜索关键词时，根据当前筛选条件刷新笔记列表
        console.log('[NoteListContext] 无搜索关键词，根据筛选条件刷新笔记列表');

        // 特殊处理：当 selectedFolder 为 'kb' 时，直接重新选择知识库来刷新
        if (getSelectedFolder() === 'kb' && getSelectedKB()) {
          console.log('[NoteListContext] 知识库筛选模式，重新选择知识库来刷新:', getSelectedKB().name);
          // 直接调用 handleKBSelect 来重新加载知识库笔记，保持文件夹选择
          await handleKBSelect(getSelectedKB(), true);
        } else {
          // 其他筛选条件的处理逻辑
          const currentTags = getSelectedTag() ? [getSelectedTag().name] : [];
          const currentTopicId = getSelectedTopic() ? getSelectedTopic().topic_id : null;
          const currentKbId = getSelectedKB() ? getSelectedKB().kb_id : null;

          // 确定筛选类型
          let filterType = getFilterTypeFromFolder(getSelectedFolder());
          // 特殊处理：如果是主题筛选，使用 'all' 类型，但传递 topicId
          if (getSelectedFolder() === 'topic' && getSelectedTopic()) {
            filterType = 'all';
          }

          console.log('[NoteListContext] 筛选条件:', {
            tags: currentTags,
            topicId: currentTopicId,
            kbId: currentKbId,
            filterType: filterType
          });

          await fetchNotes(1, pageLimit, currentTags, true, currentTopicId, false, filterType, null, currentKbId);
        }
      }

      message.success(t('messages:note_delete_success', { defaultValue: 'Note deleted successfully' }));
      setIsDeleteModalVisible(false);

      // 通知父组件更新整个 sidebar（包括统计数据、主题笔记数量、协作库笔记数量等）
      // 使用 ref 中的回调，这会调用 refreshKnowledgeBasesAndTopics 来刷新所有数据
      if (noteOperationCallbackRef.current && noteOperationCallbackRef.current.onOperationComplete) {
        console.log('[NoteListContext] 删除笔记成功，通知父组件刷新整个 sidebar...');
        await noteOperationCallbackRef.current.onOperationComplete();
        console.log('[NoteListContext] sidebar 刷新完成');
      } else {
        console.log('[NoteListContext] noteOperationCallback 回调函数未设置或格式不正确，无法刷新 sidebar');
      }
    } catch (error) {
      console.error('删除笔记失败:', error);
      message.error(t('messages:note_delete_failed', { defaultValue: 'Failed to delete note' }));
      setIsDeleteModalVisible(false);
    }
  }, [deleteNote, search, semanticSearch, searchTypeSelection, privateTopics, collaborativeKBTopics, fetchPrivateTopicNotesCount, fetchCollaborativeTopicNotesCount]); // 移除 fetchNotes 依赖，避免无限循环

  // Handle delete modal cancel
  const handleDeleteModalCancel = useCallback(() => {
    setIsDeleteModalVisible(false);
  }, []);

  // Handle note deletion (now shows confirmation modal)
  const handleDeleteNote = useCallback(() => {
    showDeleteConfirmModal();
  }, [showDeleteConfirmModal]);

  // 优化：处理置顶/取消置顶后的笔记选择逻辑
  const handlePinStateChange = useCallback(async (noteId, changeType) => {
    console.log('[NoteListContext] 处理置顶状态变化:', { noteId, changeType });

    // 获取当前选中的笔记
    const currentSelectedNote = getSelectedNote();

    // 如果当前选中的笔记就是发生变化的笔记，不需要额外处理
    if (currentSelectedNote && currentSelectedNote.id === noteId) {
      console.log('[NoteListContext] 当前选中的笔记就是发生变化的笔记，保持选择状态');
      return;
    }

    // 如果当前没有选中笔记，或者选中的笔记不是发生变化的笔记
    // 检查发生变化的笔记是否在当前显示列表中
    const changedNoteInList = displayNotes.find(note => note.id === noteId);

    if (changedNoteInList) {
      console.log('[NoteListContext] 发生变化的笔记仍在列表中，自动选中它:', noteId);
      // 自动选中发生变化的笔记，让用户看到变化效果
      handleNoteClick(changedNoteInList);
    } else {
      console.log('[NoteListContext] 发生变化的笔记不在当前列表中，保持当前选择状态');
    }
  }, [displayNotes, handleNoteClick]);

  // Handle folder selection
  const handleFolderSelect = useCallback(async (folder) => {
    console.log('[NoteList] 选择文件夹:', folder);
    console.log('[NoteList] 清理前的状态:', {
      selectedFolder: getSelectedFolder(),
      selectedTag: getSelectedTag(),
      selectedTopic: getSelectedTopic(),
      selectedKB: getSelectedKB(),
      searchKeyword: getSearchKeyword(),
      searchResultsLength: searchResults.length
    });

    // 强制清理所有相关状态
    setSelectedFolder(folder);
    setSelectedTag(null);
    setSelectedTopic(null);
    setSelectedKB(null);

    // 使用增强的清理方法，确保完全清理状态
    forceCleanAll();

    // 强制重置笔记列表状态
    resetState();

    console.log('[NoteList] 清理后的状态:', {
      selectedFolder: getSelectedFolder(),
      selectedTag: getSelectedTag(),
      selectedTopic: getSelectedTopic(),
      selectedKB: getSelectedKB(),
      searchKeyword: getSearchKeyword(),
      searchResultsLength: searchResults.length
    });

    const filterType = getFilterTypeFromFolder(folder);

    await fetchNotes(1, pageLimit, [], true, null, false, filterType, null, null);
  }, [forceCleanAll]); // 添加forceCleanAll依赖

  // Handle tag selection
  const handleTagSelect = useCallback(async (tag) => {
    console.log('[NoteList] 选择标签:', tag);

    // 强制清理所有相关状态
    setSelectedTag(tag);
    setSelectedFolder('all');
    setSelectedTopic(null);
    setSelectedKB(null);

    // 使用增强的清理方法，确保完全清理状态
    forceCleanAll();

    // 强制重置笔记列表状态
    resetState();

    // 标签选择时，使用 'all' 作为 filterType，因为标签是跨文件夹的
    await fetchNotes(1, pageLimit, [tag.name], true, null, false, 'all', null, null);
  }, [forceCleanAll]); // 添加forceCleanAll依赖

  // Handle topic selection
  const handleTopicSelect = useCallback(async (topic, context) => {
    console.log('[NoteList] 选择主题:', topic, context);

    // 强制清理所有相关状态
    setSelectedTopic(topic);
    setSelectedFolder('all');
    setSelectedTag(null);
    setSelectedKB(null);

    // 使用增强的清理方法，确保完全清理状态
    forceCleanAll();

    // 强制重置笔记列表状态
    resetState();

    if (getSearchKeyword() && getSearchKeyword().trim()) {
      console.log('[NoteList] 检测到搜索关键字，激活关键字搜索:', getSearchKeyword());

      // 对于混合检索模式，直接执行搜索，不再等待状态更新
      if (getSearchMode() === 'hybrid') {
        console.log('[NoteList] 混合检索模式，直接执行搜索:', getSearchKeyword().trim());

        // Determine KB ID based on context
        let kbId = null;
        if (context && context.type === 'collaborative' && context.kbId) {
          kbId = context.kbId;
        }

        // 直接执行语义搜索
        await semanticSearch(getSearchKeyword().trim(), 1, pageLimit, 'all', topic.topic_id, kbId, searchTypeSelection, true);
        return;
      }

      // Determine KB ID based on context
      let kbId = null;
      if (context && context.type === 'collaborative' && context.kbId) {
        kbId = context.kbId;
      }

      // 统一使用混合搜索模式
      semanticSearch(getSearchKeyword().trim(), 1, pageLimit, 'all', topic.topic_id, kbId, searchTypeSelection, true); // 添加reset=true参数
    } else {
      // 移除自动清空搜索结果，保持用户搜索状态
      // clearSearchResults();
      // 主题选择时，使用 'all' 作为 filterType，因为主题是跨文件夹的
      await fetchNotes(1, pageLimit, [], true, topic.topic_id, false, 'all', null, null);
    }
  }, [search, semanticSearch, forceCleanAll]); // 添加forceCleanAll依赖

  // Handle KB selection
  const handleKBSelect = useCallback(async (kb, keepFolder = false) => {
    console.log('[NoteList] 选择知识库:', kb, '保持文件夹选择:', keepFolder);

    // 强制清理所有相关状态
    setSelectedKB(kb);

    // 只有在不保持文件夹选择时才重置为 'all'
    if (!keepFolder) {
      setSelectedFolder('all');
    }

    setSelectedTag(null);
    setSelectedTopic(null);

    // 使用增强的清理方法，确保完全清理状态
    forceCleanAll();

    // 强制重置笔记列表状态
    resetState();

    if (getSearchKeyword() && getSearchKeyword().trim()) {
      console.log('[NoteList] 检测到搜索关键字，激活关键字搜索:', getSearchKeyword());

      // 对于混合检索模式，直接执行搜索，不再等待状态更新
      if (getSearchMode() === 'hybrid') {
        console.log('[NoteList] 混合检索模式，直接执行搜索:', getSearchKeyword().trim());

        // 直接执行语义搜索
        await semanticSearch(getSearchKeyword().trim(), 1, pageLimit, 'all', '', kb.kb_id, searchTypeSelection, true);
        return;
      }

      // 统一使用混合搜索模式
      semanticSearch(getSearchKeyword().trim(), 1, pageLimit, 'all', '', kb.kb_id, searchTypeSelection, true); // 添加reset=true参数
    } else {
      // 移除自动清空搜索结果，保持用户搜索状态
      // clearSearchResults();
      // 知识库选择时，使用 'all' 作为 filterType，因为知识库是跨文件夹的
      await fetchNotes(1, pageLimit, [], true, null, false, 'all', null, kb.kb_id);
    }
  }, [search, semanticSearch, forceCleanAll]); // 添加forceCleanAll依赖

  // Handle search
  const handleSearch = useCallback(async (value, forceSearchMode = null) => {
    const currentSearchMode = forceSearchMode || getSearchMode();
    console.log('[NoteList] 执行搜索:', value, '搜索模式:', currentSearchMode);

    if (value && value.trim()) {
      const topicId = getSelectedTopic() ? getSelectedTopic().topic_id : '';
      const kbId = getSelectedKB() ? getSelectedKB().kb_id : null;
      const filterType = getFilterTypeFromFolder(getSelectedFolder());

      // 统一使用混合搜索模式
      console.log('[NoteList] 混合检索模式，直接执行搜索:', value.trim());
      await semanticSearch(value.trim(), 1, pageLimit, filterType, topicId, kbId, searchTypeSelection, true);
    } else {
      // 只有在用户主动清空搜索关键词时才清空搜索结果
      clearSearchResults();

      // Restore previous filter state
      const currentTags = getSelectedTag() ? [getSelectedTag().name] : [];
      const currentTopicId = getSelectedTopic() ? getSelectedTopic().topic_id : null;
      const currentKbId = getSelectedKB() ? getSelectedKB().kb_id : null;
      const filterType = getFilterTypeFromFolder(getSelectedFolder());

      await fetchNotes(1, pageLimit, currentTags, true, currentTopicId, false, filterType, null, currentKbId);
    }
  }, [search, semanticSearch, clearSearchResults, getFilterTypeFromFolder, getSearchMode]);

  // Clear search
  const handleClearSearch = useCallback(() => {
    // 移除setSearchKeyword调用，避免与新逻辑冲突
    // setSearchKeyword('');
    clearSearchResults();
  }, [clearSearchResults]);

  // Handle search mode change
  const handleSearchModeChange = useCallback((mode) => {
    console.log('[NoteList] 切换搜索模式:', mode);
    setSearchMode(mode);
  }, []);

  // Handle quick action selection
  const handleQuickActionSelect = useCallback(async (action) => {
    console.log('[NoteList] 快捷操作选择:', action);

    if (getSelectedFolder() === action.key) {
      console.log('[NoteList] 当前已选中该文件夹，不重复请求');
      return;
    }

    // 设置快捷操作切换标志
    isQuickActionSwitchingRef.current = true;

    try {
      // 强制清理所有相关状态
      setSelectedFolder(action.key);
      setSelectedTag(null);
      setSelectedTopic(null);
      setSelectedKB(null);
      setIsUserSelectedNote(false); // 重置用户选择标志

      // 使用增强的清理方法，确保完全清理状态
      forceCleanAll();

      // 强制重置笔记列表状态
      resetState();

      // 添加延迟，确保状态更新完成后再执行后续操作
      await new Promise(resolve => setTimeout(resolve, 0));

      if (getSearchKeyword() && getSearchKeyword().trim()) {
        console.log('[NoteList] 检测到搜索关键字，激活关键字搜索:', getSearchKeyword());

        // 对于混合检索模式，直接执行搜索，不再等待状态更新
        if (getSearchMode() === 'hybrid') {
          console.log('[NoteList] 混合检索模式，直接执行搜索:', getSearchKeyword().trim());

          const filterType = getFilterTypeFromFolder(action.key);
          await semanticSearch(getSearchKeyword().trim(), 1, pageLimit, filterType, '', '', searchTypeSelection, true);
          return;
        }

        // 统一使用混合搜索模式
        const filterType = getFilterTypeFromFolder(action.key);
        semanticSearch(getSearchKeyword().trim(), 1, pageLimit, filterType, '', '', searchTypeSelection, true); // 添加reset=true参数
      } else {
        // 移除自动清空搜索结果，保持用户搜索状态
        // clearSearchResults();
        const filterType = getFilterTypeFromFolder(action.key);
        await fetchNotes(1, pageLimit, [], true, null, false, filterType, null, null);
      }
    } finally {
      // 清除快捷操作切换标志
      setTimeout(() => {
        isQuickActionSwitchingRef.current = false;
        console.log('[NoteList] 快捷操作切换完成，清除切换标志');
      }, 100); // 延迟100ms清除，确保所有状态更新完成
    }
  }, [getSelectedFolder(), search, semanticSearch, setIsUserSelectedNote, getFilterTypeFromFolder, forceCleanAll, resetState, fetchNotes]); // 添加resetState和fetchNotes依赖



  // 执行全文搜索（已废弃，统一使用混合搜索）
  // const executeFulltextSearch = useCallback(async (keyword, folder, tag, topic, kb) => {
  //   // 此函数已废弃，统一使用 executeVectorSearch (混合搜索)
  // }, []);

  // 执行向量搜索
  const executeVectorSearch = useCallback(async (keyword, folder, tag, topic, kb) => {
    const topicId = topic ? topic.topic_id : '';
    let kbId = kb ? kb.kb_id : null;

    // 如果有主题但没有知识库，尝试从主题上下文中获取知识库ID
    if (topicId && !kbId) {
      // 检查当前选中的主题是否来自协作库
      const currentTopic = getSelectedTopic();
      if (currentTopic && currentTopic.topic_id === topicId) {
        // 从协作库主题中查找对应的知识库
        for (const [collabKbId, topics] of Object.entries(collaborativeKBTopics || {})) {
          if (topics.some(t => t.topic_id === topicId)) {
            kbId = collabKbId;
            console.log('[NoteListContext] 从协作库主题中获取到知识库ID:', kbId);
            break;
          }
        }

        // 如果在协作库中没找到，检查私有库
        if (!kbId) {
          const privateTopic = privateTopics?.find(t => t.topic_id === topicId);
          if (privateTopic) {
            // 私有库主题，kbId 可以保持为 null，后端会处理
            console.log('[NoteListContext] 主题来自私有库，kbId 保持为 null');
          }
        }
      }
    }

    // 将 folder 转换为对应的 filterType
    const filterType = getFilterTypeFromFolder(folder);

    console.log('[NoteListContext] 执行向量搜索:', { keyword, folder, tag, topic, kb, filterType, topicId, kbId });
    const result = await semanticSearch(keyword.trim(), 1, pageLimit, filterType, topicId, kbId, searchTypeSelection, true); // 添加reset=true参数

    // 添加分数信息日志
    if (result && result.items && result.items.length > 0) {
      console.log('[NoteListContext] 向量搜索结果分数信息:');
      result.items.forEach((item, index) => {
        console.log(`  ${index + 1}. "${item.title}" - 归一化分数: ${item._score?.toFixed(4)}, 原始分数: ${item._originalScore?.toFixed(4)}`);
      });
    }

    return result;
  }, [semanticSearch, getFilterTypeFromFolder, collaborativeKBTopics, privateTopics]);

  // 执行普通查询
  const executeQuery = useCallback(async (folder, tag, topic, kb) => {
    const currentTags = tag ? [tag.name] : [];
    const currentTopicId = topic ? topic.topic_id : null;
    const currentKbId = kb ? kb.kb_id : null;
    const filterType = getFilterTypeFromFolder(folder);

    console.log('[NoteListContext] 执行普通查询:', { folder, tag, topic, kb, filterType });
    await fetchNotes(1, pageLimit, currentTags, true, currentTopicId, false, filterType, null, currentKbId);
  }, [fetchNotes]);

  // 根据全局状态自动执行搜索或查询
  const executeSearchOrQuery = useCallback(async () => {
    // 检查是否正在切换快捷操作，如果是则跳过执行
    if (isQuickActionSwitchingRef.current) {
      console.log('[NoteListContext] 检测到快捷操作切换中，跳过executeSearchOrQuery执行');
      return;
    }

    const currentSearchKeyword = getSearchKeyword();
    const currentSearchMode = getSearchMode();
    const currentFolder = getSelectedFolder();
    const currentTag = getSelectedTag();
    const currentTopic = getSelectedTopic();
    const currentKB = getSelectedKB();

    // 添加调试信息，检查知识库状态
    console.log('[NoteListContext] executeSearchOrQuery 当前状态:', {
      currentSearchKeyword,
      currentSearchMode: 'hybrid', // 统一使用混合搜索模式
      currentFolder,
      currentTag: currentTag?.name,
      currentTopic: currentTopic?.name,
      currentKB: currentKB?.name,
      currentKBId: currentKB?.kb_id,
      currentKBType: typeof currentKB,
      currentKBKeys: currentKB ? Object.keys(currentKB) : 'null'
    });

    // 防重复调用：检查是否与上次搜索参数相同
    const currentSearchParams = {
      keyword: currentSearchKeyword,
      mode: 'hybrid', // 统一使用混合搜索模式
      folder: currentFolder,
      tag: currentTag,
      topic: currentTopic,
      kb: currentKB
    };

    // 检查参数是否相同
    if (lastSearchParamsRef.current &&
      JSON.stringify(lastSearchParamsRef.current) === JSON.stringify(currentSearchParams)) {
      console.log('[NoteListContext] 检测到重复搜索参数，跳过执行:', currentSearchParams);
      return;
    }

    // 更新上次搜索参数
    lastSearchParamsRef.current = currentSearchParams;

    if (currentSearchKeyword && currentSearchKeyword.trim()) {
      console.log('[NoteListContext] 检测到搜索关键词，执行搜索:', currentSearchParams);

      // 统一使用混合搜索模式（向量检索）
      const result = await executeVectorSearch(currentSearchKeyword, currentFolder, currentTag, currentTopic, currentKB);
      console.log('[NoteListContext] 混合检索完成，结果分数信息:', {
        totalItems: result?.items?.length || 0,
        scores: result?.items?.map(item => ({
          title: item.title,
          normalizedScore: item._score,
          originalScore: item._originalScore,
          vectorOriginalScore: item._vectorOriginalScore
        })) || []
      });

      // 检查第一个结果的完整结构
      if (result?.items && result.items.length > 0) {
        console.log('[NoteListContext] 第一个结果的完整结构:', result.items[0]);
        console.log('[NoteListContext] 第一个结果的分数字段:', {
          _score: result.items[0]._score,
          _originalScore: result.items[0]._originalScore,
          vectorOriginalScore: result.items[0]._vectorOriginalScore,
          hasScore: '_score' in result.items[0],
          hasOriginalScore: '_originalScore' in result.items[0],
          hasVectorOriginalScore: '_vectorOriginalScore' in result.items[0]
        });
      }

      // 同步搜索分页状态到全局分页状态
      if (result) {
        const itemsCount = result.items?.length || 0;
        const total = result.total || 0;
        const page = result.page || 1;
        const limit = result.limit || pageLimit;
        
        // 计算 hasMore：
        // 1. 如果返回结果为空，hasMore = false
        // 2. 否则，根据 total > (page * limit) 判断是否还有更多数据
        // 注意：不根据 itemsCount < limit 来判断，因为后台可能会过滤笔记，导致返回结果少于 limit
        const hasMore = itemsCount > 0 && total > (page * limit);
        
        const searchPagination = {
          total: total,
          page: page,
          limit: limit,
          hasMore: hasMore,
          isLoading: false
        };
        console.log('[NoteListContext] 设置搜索分页状态:', {
          itemsCount,
          total,
          page,
          limit,
          hasMore,
          calculation: `itemsCount(${itemsCount}) > 0 && total(${total}) > (page(${page}) * limit(${limit}))`
        });
        setPagination(searchPagination, 'noteStore.executeSearchOrQuery.hybrid');
      }
    } else {
      console.log('[NoteListContext] 无搜索关键词，执行普通查询:', {
        folder: currentFolder,
        tag: currentTag,
        topic: currentTopic,
        kb: currentKB
      });

      // 执行普通查询逻辑
      await executeQuery(currentFolder, currentTag, currentTopic, currentKB);
    }
  }, [executeVectorSearch, executeQuery]);

  // 使用useEffect监听全局状态变化，自动执行搜索或查询
  useEffect(() => {
    console.log('[NoteListContext] useEffect 触发，设置全局状态监听器');
    console.log('[NoteListContext] 当前状态:', {
      searchKeyword: getSearchKeyword(),
      searchMode: getSearchMode(),
      selectedFolder: getSelectedFolder(),
      selectedTag: getSelectedTag(),
      selectedTopic: getSelectedTopic(),
      selectedKB: getSelectedKB()
    });

    // 注意：不在这里直接调用executeSearchOrQuery，而是让全局状态监听器处理
    // 这样可以避免重复调用

    // 使用全局状态监听器来监听状态变化
    const removeListener = addGlobalStateListener(async (newState, oldState, updates) => {
      // 检查是否有相关的状态变化
      if (updates.selectedFolder || updates.selectedTag || updates.selectedTopic || updates.selectedKB || updates.searchKeyword || updates.searchMode || updates.noteStateChange) {
        console.log('[NoteListContext] 检测到全局状态变化，重新执行搜索或查询:', {
          updates,
          oldState: {
            selectedFolder: oldState.selectedFolder,
            selectedTag: oldState.selectedTag,
            selectedTopic: oldState.selectedTopic,
            selectedKB: oldState.selectedKB,
            searchKeyword: oldState.searchKeyword,
            searchMode: oldState.searchMode
          },
          newState: {
            selectedFolder: newState.selectedFolder,
            selectedTag: newState.selectedTag,
            selectedTopic: newState.selectedTopic,
            selectedKB: newState.selectedKB,
            searchKeyword: newState.searchKeyword,
            searchMode: newState.searchMode
          }
        });

        // 处理搜索关键词变化的情况
        if (updates.searchKeyword) {
          console.log('[NoteListContext] 检测到搜索关键词变化:', {
            oldKeyword: oldState.searchKeyword,
            newKeyword: newState.searchKeyword,
            hasOtherUpdates: !!(updates.selectedFolder || updates.selectedTag || updates.selectedTopic || updates.selectedKB)
          });

          // 如果只是搜索关键词变化（没有其他筛选条件变化），由 handleSearch 处理
          if (!(updates.selectedFolder || updates.selectedTag || updates.selectedTopic || updates.selectedKB || updates.searchMode)) {
            console.log('[NoteListContext] 仅搜索关键词变化，跳过全局状态监听器的搜索执行');
            return;
          }

          // 如果搜索关键词和筛选条件同时变化，继续执行搜索逻辑
          console.log('[NoteListContext] 搜索关键词和筛选条件同时变化，继续执行搜索逻辑');
        }

        // 处理笔记状态变化，触发混合搜索更新
        if (updates.noteStateChange) {
          console.log('[NoteListContext] 检测到笔记状态变化:', updates.noteStateChange);
          console.log('[NoteListContext] 当前搜索状态:', {
            searchKeyword: newState.searchKeyword,
            searchMode: newState.searchMode,
            hasSearchKeyword: newState.searchKeyword && newState.searchKeyword.trim()
          });

          // 如果有搜索关键词，强制触发搜索更新
          if (newState.searchKeyword && newState.searchKeyword.trim()) {
            console.log('[NoteListContext] 强制触发搜索更新:', newState.searchKeyword);

            // 使用 setTimeout 异步执行搜索，避免阻塞监听器
            // 对于删除操作，先清空搜索结果，然后重新搜索
            if (updates.noteStateChange.changeType === 'delete') {
              console.log('[NoteListContext] 删除操作检测到，先清空搜索结果');
              try {
                // 清空搜索结果
                const { clearSearchResults } = await import('../stores/searchStore');
                clearSearchResults();
                console.log('[NoteListContext] 搜索结果已清空');
              } catch (error) {
                console.error('[NoteListContext] 清空搜索结果失败:', error);
              }
            }

            setTimeout(() => {
              try {
                const topicId = newState.selectedTopic ? newState.selectedTopic.topic_id : '';
                const kbId = newState.selectedKB ? newState.selectedKB.kb_id : null;
                const filterType = getFilterTypeFromFolder(newState.selectedFolder);

                console.log('[NoteListContext] 开始执行搜索更新，参数:', {
                  keyword: newState.searchKeyword.trim(),
                  mode: newState.searchMode,
                  filterType,
                  topicId,
                  kbId
                });

                // 统一使用混合搜索模式
                console.log('[NoteListContext] 执行混合搜索更新');
                semanticSearch(newState.searchKeyword.trim(), 1, pageLimit, filterType, topicId, kbId, searchTypeSelection, true)
                  .then((result) => {
                    console.log('[NoteListContext] 混合搜索更新完成，结果:', {
                      total: result?.total,
                      itemsCount: result?.items?.length,
                      filterType,
                      keyword: newState.searchKeyword.trim()
                    });
                  })
                  .catch(error => console.error('[NoteListContext] 混合搜索更新失败:', error));
              } catch (error) {
                console.error('[NoteListContext] 搜索更新执行失败:', error);
              }
            }, 100); // 使用固定的100ms延迟，避免阻塞监听器

            // 直接返回，不等待搜索完成
            return;
          }

          // 处理非搜索状态下的笔记更新和刷新
          if (updates.noteStateChange.changeType === 'update' || updates.noteStateChange.changeType === 'refresh') {
            console.log('[NoteListContext] 检测到笔记更新/刷新，刷新笔记列表:', updates.noteStateChange.changeType);

            // 延迟执行，确保笔记数据已更新
            setTimeout(() => {
              try {
                console.log('[NoteListContext] 开始刷新笔记列表，当前筛选条件:', {
                  selectedFolder: newState.selectedFolder,
                  selectedTag: newState.selectedTag?.name,
                  selectedTopic: newState.selectedTopic?.name,
                  selectedKB: newState.selectedKB?.name
                });

                // 获取当前筛选条件
                const currentTags = newState.selectedTag ? [newState.selectedTag.name] : [];
                const currentTopicId = newState.selectedTopic ? newState.selectedTopic.topic_id : null;
                const currentKbId = newState.selectedKB ? newState.selectedKB.kb_id : null;
                const filterType = getFilterTypeFromFolder(newState.selectedFolder);

                console.log('[NoteListContext] 使用筛选条件刷新笔记列表:', {
                  filterType,
                  currentTags,
                  currentTopicId,
                  currentKbId
                });

                // 调用 fetchNotes 刷新笔记列表
                fetchNotes(1, pageLimit, currentTags, true, currentTopicId, false, filterType, null, currentKbId);

                console.log('[NoteListContext] 笔记列表刷新完成');
              } catch (error) {
                console.error('[NoteListContext] 刷新笔记列表失败:', error);
              }
            }, 200); // 延迟200ms，确保笔记数据已更新

            return;
          }
        }

        // 检查是否正在切换快捷操作，如果是则跳过执行
        if (isQuickActionSwitchingRef.current) {
          console.log('[NoteListContext] 检测到快捷操作切换中，跳过全局状态监听器的搜索执行');
          return;
        }

        // 防重复执行：检查是否与上次执行的状态相同
        const currentState = {
          selectedFolder: newState.selectedFolder,
          selectedTag: newState.selectedTag,
          selectedTopic: newState.selectedTopic,
          selectedKB: newState.selectedKB,
          searchMode: newState.searchMode,
          searchKeyword: newState.searchKeyword
        };

        if (lastExecutedStateRef.current &&
          JSON.stringify(lastExecutedStateRef.current) === JSON.stringify(currentState)) {
          console.log('[NoteListContext] 检测到重复状态，跳过执行搜索或查询:', currentState);
          return;
        }

        // 更新上次执行的状态
        lastExecutedStateRef.current = currentState;

        // 执行搜索或查询
        executeSearchOrQuery();
      }
    });

    // 清理监听器
    return removeListener;
  }, []); // 只在组件挂载时执行一次

  // 自动选中新建的笔记（刷新后）
  useEffect(() => {
    // 如果已经处理过，跳过
    if (autoSelectNoteRef.current) {
      return;
    }

    // 检查是否有新建笔记的ID需要选中
    const createNoteId = localStorage.getItem('createNoteId');
    if (createNoteId && displayNotes.length > 0) {
      console.log('[NoteListContext] 检测到新建笔记ID，尝试自动选中:', createNoteId);
      
      // 在笔记列表中查找对应的笔记
      const targetNote = displayNotes.find(note => {
        const noteIdStr = note.id?.toString();
        const createNoteIdStr = createNoteId.toString();
        return noteIdStr === createNoteIdStr || note.id === createNoteId;
      });
      
      if (targetNote) {
        console.log('[NoteListContext] 找到新建笔记，自动选中:', targetNote.id, targetNote.title);
        // 标记已处理，避免重复
        autoSelectNoteRef.current = true;
        // 延迟一下，确保笔记列表已完全渲染
        setTimeout(() => {
          handleNoteClick(targetNote);
          // 清除标志，避免重复选中
          localStorage.removeItem('createNoteId');
        }, 500);
      } else {
        console.log('[NoteListContext] 新建笔记尚未加载到列表中，等待加载...');
        // 如果笔记还没加载，等待一段时间后重试（最多等待3秒）
        let retryCount = 0;
        const maxRetries = 6; // 6次 * 500ms = 3秒
        const retryInterval = setInterval(() => {
          retryCount++;
          const note = displayNotes.find(n => {
            const noteIdStr = n.id?.toString();
            const createNoteIdStr = createNoteId.toString();
            return noteIdStr === createNoteIdStr || n.id === createNoteId;
          });
          if (note) {
            console.log('[NoteListContext] 重试后找到新建笔记，自动选中:', note.id, note.title);
            autoSelectNoteRef.current = true;
            handleNoteClick(note);
            localStorage.removeItem('createNoteId');
            clearInterval(retryInterval);
          } else if (retryCount >= maxRetries) {
            console.log('[NoteListContext] 等待超时，未找到新建笔记，清除标志');
            autoSelectNoteRef.current = true; // 标记已处理，即使没找到也停止重试
            localStorage.removeItem('createNoteId');
            clearInterval(retryInterval);
          }
        }, 500);
        
        // 清理定时器（如果组件卸载）
        return () => {
          clearInterval(retryInterval);
        };
      }
    } else if (createNoteId && displayNotes.length === 0) {
      // 如果笔记列表为空，等待列表加载
      console.log('[NoteListContext] 笔记列表为空，等待加载...');
    }
  }, [displayNotes, handleNoteClick]); // 依赖displayNotes，当笔记列表更新时检查

  // Load more notes
  const handleLoadMore = useCallback(async () => {
    if (!getPagination().hasMore || getPagination().isLoading) return;

    // 检查是否有搜索关键词
    const currentSearchKeyword = getSearchKeyword();

    if (currentSearchKeyword && currentSearchKeyword.trim()) {
      // 有搜索关键词时，加载更多搜索结果
      console.log('[handleLoadMore] 加载更多搜索结果:', currentSearchKeyword);

      const topicId = getSelectedTopic() ? getSelectedTopic().topic_id : '';
      const kbId = getSelectedKB() ? getSelectedKB().kb_id : null;
      const filterType = getFilterTypeFromFolder(getSelectedFolder());

      // 统一使用混合搜索模式
      await loadMoreSearchResults(
        currentSearchKeyword.trim(),
        filterType,
        topicId,
        kbId,
        'hybrid'
      );
    } else {
      // 没有搜索关键词时，加载更多标准查询结果
      console.log('[handleLoadMore] 加载更多标准查询结果');

      const currentTags = getSelectedTag() ? [getSelectedTag().name] : [];
      const currentTopicId = getSelectedTopic() ? getSelectedTopic().topic_id : null;
      const currentKbId = getSelectedKB() ? getSelectedKB().kb_id : null;
      const filterType = getFilterTypeFromFolder(getSelectedFolder());

      await loadMoreNotes(currentTags, currentTopicId, false, filterType, currentKbId);
    }
  }, [getPagination().hasMore, getPagination().isLoading, getSearchKeyword, getSearchMode, getSelectedTopic, getSelectedKB, getSelectedFolder, getFilterTypeFromFolder, loadMoreSearchResults, loadMoreNotes]);

  const value = {
    // Note data
    notes,
    pinnedNotes,
    displayNotes,
    selectedNote: getSelectedNote(),
    isUserSelectedNote: getIsUserSelectedNote(),
    lastFetchedNoteId: null, // This will be managed by the global state

    // Search and filter state
    searchKeyword: getSearchKeyword(),
    searchMode: getSearchMode(),
    selectedFolder: getSelectedFolder(),
    selectedTag: getSelectedTag(),
    selectedTopic: getSelectedTopic(),
    selectedKB: getSelectedKB(),
    searchResults,

    // Loading states
    isLoading: getPagination().isLoading,
    isSearchLoading,
    pagination: getPagination(),

    // Refs
    noteListRef,

    // State setters
    setSelectedNote,
    setIsUserSelectedNote,
    setLastFetchedNoteId: () => { }, // This will be managed by the global state
    setSearchKeyword,
    setSearchMode,
    setSelectedFolder,
    setSelectedTag,
    setSelectedTopic,
    setSelectedKB,

    // Action handlers
    handleNoteClick,
    handleDeleteNote,
    handlePinStateChange,
    handleFolderSelect,
    handleTagSelect,
    handleTopicSelect,
    handleKBSelect,
    handleSearch,
    handleClearSearch,
    handleSearchModeChange,
    handleQuickActionSelect,
    handleLoadMore,

    // Store actions
    fetchNotes,
    loadMoreNotes,
    updateNote,
    resetState,
    clearSearchResults,

    // Sidebar update callback
    setNoteOperationCallback,
    executeNoteOperationCallback,

    // Delete modal state
    isDeleteModalVisible
  };

  return (
    <NoteListContext.Provider value={value}>
      {children}
      <DeleteConfirmModal
        isVisible={isDeleteModalVisible}
        noteTitle={getSelectedNote() ? getSelectedNote().title : ''}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteModalCancel}
      />
    </NoteListContext.Provider>
  );
};